import { convertDateFormat, titleCaseString } from "@/utils";

/** The standard Copy Details template, in paste order; the backend stores a company's chosen keys opaquely. */
export const COPY_DETAILS_FIELDS = [
  {
    key: "patient_name",
    label: "Patient Name",
    value: (order) => [order.patientFirstName, order.patientLastName].filter(Boolean).join(" "),
  },
  {
    key: "patient_dob",
    label: "Patient DOB",
    value: (order) => (order.patientDob ? convertDateFormat(order.patientDob) : ""),
  },
  { key: "status", label: "Status", value: (order) => order.status || "" },
  {
    key: "date_created",
    label: "Date Created",
    value: (order) => (order.createdOn ? convertDateFormat(order.createdOn) : ""),
  },
  {
    key: "date_modified",
    label: "Date Modified",
    value: (order) => (order.modifiedOn ? convertDateFormat(order.modifiedOn) : ""),
  },
  { key: "lamar_health_key", label: "Lamar Health Key", value: (order) => order.orderKey || "" },
  {
    key: "external_portal_key",
    label: "External Portal Key",
    value: (order) => order.orderSubmission?.portalSubmissionId || "",
  },
  {
    key: "medication",
    label: "Medication",
    value: (order) => (order.medication ? titleCaseString(order.medication) : ""),
  },
  {
    key: "insurance_form_name",
    label: "Insurance/Form Name",
    value: (order) => (order.insuranceProvider ? titleCaseString(order.insuranceProvider) : ""),
  },
  { key: "plan_type", label: "Plan Type", value: (order) => order.insurancePlanType || "" },
  {
    key: "prior_auth_number",
    label: "Prior Auth Number",
    value: (order) => order.orderSubmission?.priorAuthNumber || "",
  },
  {
    key: "expires_on",
    label: "Expires On",
    value: (order) =>
      order.orderSubmission?.priorAuthExpiration ? convertDateFormat(order.orderSubmission.priorAuthExpiration) : "",
  },
  { key: "rx_number", label: "Rx Number", value: (order) => order.prescriptionNumber || "" },
  { key: "mrn", label: "MRN", value: (order) => order.patientExternalId || "" },
];

export const STANDARD_COPY_DETAILS_FIELDS = COPY_DETAILS_FIELDS.map((field) => field.key);

/**
 * Format a submission's details as "Label: Value" lines for pasting into
 * external systems (LAM-4465), limited to the company's chosen fields (LAM-5751).
 * Operates on the nested order shape returned by both the order-detail and
 * order-list APIs — grid rows must pass `row.raw`. Empty fields are kept with blank values.
 */
export function formatSubmissionDetails(order, fieldKeys = STANDARD_COPY_DETAILS_FIELDS) {
  const selected = COPY_DETAILS_FIELDS.filter((field) => fieldKeys.includes(field.key));
  return (selected.length ? selected : COPY_DETAILS_FIELDS)
    .map((field) => `${field.label}: ${field.value(order)}`)
    .join("\n");
}

/** Copy text to the clipboard; falls back to execCommand when the async API is unavailable or denied. */
export async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    try {
      textarea.select();
      return document.execCommand("copy");
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }
}
